import { path } from 'rambda';
import { useTranslation } from 'react-i18next';
import Simple66LogoH160Picture from '@/assets/images/brands/ryuryu/logo.png?h=160&format=avif;webp;png&as=picture';
import Full66LogoW40Picture from '@/assets/images/brands/ryuryu/logo-full.png?w=40;80;120&format=avif;webp;png&as=picture';
import Full66LogoW160Picture from '@/assets/images/brands/ryuryu/logo-full.png?w=160&format=avif;webp;png&as=picture';
import SimpleWYLogoHanH160Picture from '@/assets/images/brands/wangyou/logo.han.png?h=160&format=avif;webp;png&as=picture';
import SimpleWYLogoLatinH160Picture from '@/assets/images/brands/wangyou/logo.latin.png?h=160&format=avif;webp;png&as=picture';
import FullWYLogoHanW40Picture from '@/assets/images/brands/wangyou/logo-full.han.png?w=40;80;120&format=avif;webp;png&as=picture';
import FullWYLogoHanW160Picture from '@/assets/images/brands/wangyou/logo-full.han.png?w=160&format=avif;webp;png&as=picture';
import FullWYLogoLatinW40Picture from '@/assets/images/brands/wangyou/logo-full.latin.png?w=40;80;120&format=avif;webp;png&as=picture';
import FullWYLogoLatinW160Picture from '@/assets/images/brands/wangyou/logo-full.latin.png?w=160&format=avif;webp;png&as=picture';
import PicturizedImage, { type PicturizedImageProps } from '@/components/common/picturized-image';
import { useBrand } from '@/hooks/pwa/use-brand';
import type { BrandKey } from '@/models/configuration';
import { type GlyphCode, transformGlyphCode } from '@/utils/language-code';

type LogoVariant = 'full-w160' | 'full-w40' | 'simple-h160';

const brandLogoMap: Record<BrandKey, Record<LogoVariant, Record<GlyphCode, Picture> | Picture>> = {
  ryuryu: {
    'full-w160': Full66LogoW160Picture,
    'full-w40': Full66LogoW40Picture,
    'simple-h160': Simple66LogoH160Picture,
  },
  wangyou: {
    'full-w160': { han: FullWYLogoHanW160Picture, latin: FullWYLogoLatinW160Picture },
    'full-w40': { han: FullWYLogoHanW40Picture, latin: FullWYLogoLatinW40Picture },
    'simple-h160': { han: SimpleWYLogoHanH160Picture, latin: SimpleWYLogoLatinH160Picture },
  },
};

interface Props {
  variant: LogoVariant;
  fetchPriority?: 'high' | 'low' | 'auto';
  as?: PicturizedImageProps['as'];
  className?: string;
}

export function BrandLogo({ className, as, fetchPriority, variant }: Props) {
  const { i18n } = useTranslation();
  const brand = useBrand();

  if (!brand.key) return <div className={className} />;

  let picture = path([brand.key, variant], brandLogoMap);

  if (typeof picture === 'object' && 'han' in picture) {
    const glyphCode = transformGlyphCode(i18n.resolvedLanguage);
    picture = path([glyphCode], picture);
  }

  if (picture === undefined) return <div className={className} />;

  return (
    <PicturizedImage
      fetchPriority={fetchPriority}
      as={as}
      picture={picture}
      className={className}
    />
  );
}
